import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBrand } from '../contexts/BrandContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { formatApiError } from '../lib/utils';
import { Headphones, ArrowLeft, ArrowRight, Ticket, Camera, X, Mail, Loader2, Check, Keyboard } from 'lucide-react';
import { toast } from 'sonner';

function QrScanner({ onScan, onClose }) {
    const scannerRef = useRef(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let html5QrCode = null;
        const startScanner = async () => {
            try {
                const { Html5Qrcode } = await import('html5-qrcode');
                html5QrCode = new Html5Qrcode('qr-reader-access');
                scannerRef.current = html5QrCode;
                await html5QrCode.start(
                    { facingMode: 'environment' },
                    { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1 },
                    (decodedText) => {
                        let code = decodedText;
                        try {
                            const url = new URL(decodedText);
                            const urlCode = url.searchParams.get('code') || url.pathname.split('/').pop();
                            if (urlCode) code = urlCode;
                        } catch {}
                        onScan(code.toUpperCase());
                    },
                    () => {}
                );
            } catch {
                setError('Camera access denied. Please enter the code manually.');
            }
        };
        startScanner();
        return () => { if (scannerRef.current) scannerRef.current.stop().catch(() => {}); };
    }, [onScan]);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-white font-medium flex items-center gap-2">
                    <Camera className="w-5 h-5 text-gold" /> Scan QR Code
                </h3>
                <Button variant="ghost" size="sm" onClick={onClose} className="text-muted-foreground hover:text-white">
                    <X className="w-4 h-4" />
                </Button>
            </div>
            {error ? (
                <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-sm text-center">{error}</div>
            ) : (
                <div className="relative rounded-xl overflow-hidden border-2 border-gold/30 aspect-square max-w-[320px] mx-auto">
                    <div id="qr-reader-access" className="w-full h-full" style={{ minHeight: '280px' }} />
                    <p className="text-center text-xs text-muted-foreground py-2 bg-[#0A0D14]">Point your camera at the QR code on your ticket</p>
                </div>
            )}
        </div>
    );
}

export default function TravelerAccessPage() {
    const [searchParams] = useSearchParams();
    const brand = useBrand();
    const { travelerAccess, travelerReturn } = useAuth();
    const navigate = useNavigate();

    // State
    const [mode, setMode] = useState('new'); // 'new' or 'return'
    const [step, setStep] = useState(1); // Step 1: code, Step 2: email, Step 3: success
    const [code, setCode] = useState('');
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showScanner, setShowScanner] = useState(false);

    useEffect(() => {
        const urlCode = searchParams.get('code');
        if (urlCode) {
            setCode(urlCode.toUpperCase());
            setStep(2); // Skip to email step
        }
    }, [searchParams]);

    const handleCodeSubmit = (e) => {
        e.preventDefault();
        if (!code.trim()) { toast.error('Please enter a voucher code'); return; }
        setError('');
        setStep(2);
    };

    const handleAccessSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) { toast.error('Please enter your email'); return; }
        setLoading(true);
        setError('');
        try {
            await travelerAccess(code.trim(), email.trim());
            setStep(3);
            toast.success('Welcome! Your audio guide is ready.');
            setTimeout(() => navigate('/my-guides'), 2000);
        } catch (err) {
            const detail = err.response?.data?.detail;
            const errorMsg = detail 
                ? (typeof detail === 'string' ? detail : JSON.stringify(detail))
                : (err.message || 'Network error. Please check your connection and try again.');
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleReturnSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim() || !code.trim()) { toast.error('Please fill in all fields'); return; }
        setLoading(true);
        setError('');
        try {
            await travelerReturn(email.trim(), code.trim());
            toast.success('Welcome back!');
            navigate('/my-guides');
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || 'Login failed. Please check your credentials.');
        } finally {
            setLoading(false);
        }
    };

    const handleScan = (scannedCode) => {
        setCode(scannedCode);
        setShowScanner(false);
        toast.success(`Code detected: ${scannedCode}`);
        setStep(2);
    };

    return (
        <div className="min-h-screen bg-[#0A0D14] flex">
            {/* Left Panel */}
            <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 lg:px-16">
                <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-white mb-12 transition-colors">
                    <ArrowLeft className="w-4 h-4" /> Back to home
                </Link>

                <div className="max-w-md">
                    {/* Logo */}
                    <div className="flex items-center gap-3 mb-8">
                        {brand.logo_url ? (
                            <img src={brand.logo_url} alt={brand.platform_name} className="h-9 max-w-[160px] object-contain" />
                        ) : (
                            <div className="w-10 h-10 rounded-md bg-gold flex items-center justify-center">
                                <Headphones className="w-5 h-5 text-[#0A0D14]" />
                            </div>
                        )}
                        {!brand.logo_url && <span className="font-outfit font-semibold text-xl text-white">{brand.platform_name}</span>}
                    </div>

                    {/* Mode Toggle */}
                    {step < 3 && (
                        <div className="flex gap-2 mb-8 p-1 bg-[#121620] rounded-lg border border-white/10">
                            <button
                                onClick={() => { setMode('new'); setStep(1); setError(''); }}
                                className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
                                    mode === 'new' ? 'bg-gold text-[#0A0D14]' : 'text-muted-foreground hover:text-white'
                                }`}
                                data-testid="mode-new-code"
                            >
                                I have a code
                            </button>
                            <button
                                onClick={() => { setMode('return'); setError(''); }}
                                className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
                                    mode === 'return' ? 'bg-gold text-[#0A0D14]' : 'text-muted-foreground hover:text-white'
                                }`}
                                data-testid="mode-return"
                            >
                                I've been here before
                            </button>
                        </div>
                    )}

                    {/* NEW CODE FLOW */}
                    {mode === 'new' && (
                        <>
                            {step === 1 && (
                                <div className="animate-fade-in">
                                    <h1 className="font-outfit text-3xl font-bold text-white mb-2">Enter your code</h1>
                                    <p className="text-muted-foreground mb-8">Scan your QR code or type the voucher code from your ticket</p>

                                    {showScanner ? (
                                        <div className="mb-6">
                                            <QrScanner onScan={handleScan} onClose={() => setShowScanner(false)} />
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => setShowScanner(true)}
                                            className="w-full p-5 rounded-xl border-2 border-dashed border-gold/30 hover:border-gold/60 transition-colors flex flex-col items-center gap-3 group mb-6"
                                            data-testid="open-qr-scanner-btn"
                                        >
                                            <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                                                <Camera className="w-6 h-6 text-gold" />
                                            </div>
                                            <p className="text-white font-medium text-sm">Scan QR Code</p>
                                        </button>
                                    )}

                                    <div className="flex items-center gap-4 mb-6">
                                        <div className="flex-1 h-px bg-white/10" />
                                        <span className="text-xs text-muted-foreground uppercase tracking-wider">or type it</span>
                                        <div className="flex-1 h-px bg-white/10" />
                                    </div>

                                    <form onSubmit={handleCodeSubmit} className="space-y-4">
                                        {error && (
                                            <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm">{error}</div>
                                        )}
                                        <div className="relative">
                                            <Keyboard className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                            <Input
                                                value={code}
                                                onChange={(e) => setCode(e.target.value.toUpperCase())}
                                                placeholder="XXXX-XXXX-XXXX"
                                                className="pl-12 h-14 text-center text-lg tracking-widest uppercase bg-[#121620] border-white/10 focus:border-gold"
                                                maxLength={20}
                                                data-testid="access-code-input"
                                            />
                                        </div>
                                        <Button
                                            type="submit"
                                            className="w-full h-12 bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2"
                                            disabled={!code.trim()}
                                            data-testid="access-code-submit"
                                        >
                                            Continue <ArrowRight className="w-4 h-4" />
                                        </Button>
                                    </form>
                                </div>
                            )}

                            {step === 2 && (
                                <div className="animate-fade-in">
                                    <button onClick={() => setStep(1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-white mb-6 transition-colors">
                                        <ArrowLeft className="w-3 h-3" /> Change code
                                    </button>

                                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gold/10 border border-gold/30 rounded-full text-gold text-sm mb-6">
                                        <Ticket className="w-4 h-4" /> {code}
                                    </div>

                                    <h1 className="font-outfit text-3xl font-bold text-white mb-2">Almost there</h1>
                                    <p className="text-muted-foreground mb-8">Enter your email so you can come back anytime</p>

                                    <form onSubmit={handleAccessSubmit} className="space-y-4">
                                        {error && (
                                            <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm">{error}</div>
                                        )}
                                        <div className="space-y-2">
                                            <Label htmlFor="email">Email address</Label>
                                            <div className="relative">
                                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                                <Input
                                                    id="email"
                                                    type="email"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    placeholder="you@example.com"
                                                    required
                                                    className="pl-12 h-12 bg-[#121620] border-white/10 focus:border-gold focus:ring-gold"
                                                    data-testid="access-email-input"
                                                />
                                            </div>
                                        </div>
                                        <Button
                                            type="submit"
                                            className="w-full h-12 bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2"
                                            disabled={loading || !email.trim()}
                                            data-testid="access-email-submit"
                                        >
                                            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Activating...</> : <>Get Access <ArrowRight className="w-4 h-4" /></>}
                                        </Button>
                                    </form>

                                    <p className="mt-4 text-xs text-muted-foreground">
                                        We'll use this to let you log back in. No password needed.
                                    </p>
                                </div>
                            )}

                            {step === 3 && (
                                <div className="text-center py-8 animate-fade-in">
                                    <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                                        <Check className="w-8 h-8 text-emerald-400" />
                                    </div>
                                    <h2 className="font-outfit font-semibold text-white text-2xl mb-2">You're in!</h2>
                                    <p className="text-muted-foreground mb-6">Your audio guide is ready. Redirecting...</p>
                                    <Button onClick={() => navigate('/my-guides')} className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" data-testid="access-go-guides">
                                        Go to My Guides <ArrowRight className="w-4 h-4" />
                                    </Button>
                                </div>
                            )}
                        </>
                    )}

                    {/* RETURNING FLOW */}
                    {mode === 'return' && (
                        <div className="animate-fade-in">
                            <h1 className="font-outfit text-3xl font-bold text-white mb-2">Welcome back</h1>
                            <p className="text-muted-foreground mb-8">Sign in with your email and any voucher code you've used before</p>

                            <form onSubmit={handleReturnSubmit} className="space-y-5">
                                {error && (
                                    <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm">{error}</div>
                                )}
                                <div className="space-y-2">
                                    <Label htmlFor="return-email">Email</Label>
                                    <Input
                                        id="return-email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        required
                                        className="bg-[#121620] border-white/10 focus:border-gold focus:ring-gold"
                                        data-testid="return-email-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="return-code">Voucher Code</Label>
                                    <Input
                                        id="return-code"
                                        value={code}
                                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                                        placeholder="XXXX-XXXX-XXXX"
                                        required
                                        className="bg-[#121620] border-white/10 focus:border-gold focus:ring-gold uppercase tracking-wider"
                                        data-testid="return-code-input"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full h-12 bg-gold text-[#0A0D14] hover:bg-gold/90"
                                    disabled={loading || !email.trim() || !code.trim()}
                                    data-testid="return-submit"
                                >
                                    {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in...</> : 'Sign In'}
                                </Button>
                            </form>
                        </div>
                    )}

                    {/* Staff login link */}
                    {step < 3 && (
                        <p className="mt-8 text-center text-muted-foreground text-sm">
                            Admin or Tour Operator?{' '}
                            <Link to="/login" className="text-gold hover:underline">Staff Sign In</Link>
                        </p>
                    )}
                </div>
            </div>

            {/* Right Panel */}
            <div className="hidden lg:block lg:w-1/2 relative">
                <img
                    src="https://images.unsplash.com/photo-1688233923543-7b972d1b171a?w=1200"
                    alt="Traveler with headphones"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0A0D14] via-transparent to-transparent" />
            </div>
        </div>
    );
}
